import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faComment,
  faTimes,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import examApi from '../../services/api/examApi';

/**
 * Enhanced feedback modal with categorized feedback options
 */
const FeedbackModal = ({
  showFeedbackModal,
  setShowFeedbackModal,
  currentQuestion,
  onSubmitFeedback,
  themeStyles,
  isDarkMode
}) => {  const [selectedCategory, setSelectedCategory] = useState('');
  const [customFeedback, setCustomFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [existingFeedback, setExistingFeedback] = useState(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState(false);
  const [isUpdatingExisting, setIsUpdatingExisting] = useState(false);
  // Predefined feedback categories
  const feedbackCategories = [
    { value: 'content-issue', label: 'Content Issue', description: 'Incorrect or unclear question content' },
    { value: 'technical-problem', label: 'Technical Problem', description: 'Loading, display, or functionality issues' },
    { value: 'suggestion', label: 'Suggestion', description: 'Ideas for improvement' },
    { value: 'difficulty-level', label: 'Difficulty Level', description: 'Question seems too easy or too hard' },
    { value: 'answer-dispute', label: 'Answer Dispute', description: 'Disagree with the correct answer' },
    { value: 'formatting', label: 'Formatting', description: 'Text formatting or display issues' },
    { value: 'other', label: 'Other', description: 'General feedback or other concerns' }
  ];

  // Check for existing feedback when modal opens
  useEffect(() => {
    const checkExistingFeedback = async () => {
      if (showFeedbackModal && currentQuestion?.id) {
        setIsLoadingFeedback(true);
        try {
          const feedback = await examApi.getQuestionFeedback(currentQuestion.id);
          if (feedback) {
            setExistingFeedback(feedback);
            setIsUpdatingExisting(true);
            
            // Parse existing feedback to extract category and text
            const feedbackText = feedback.Feedback || feedback.feedback || '';
            
            // Check if feedback starts with a category tag like [Content Issue]
            const categoryMatch = feedbackText.match(/^\[([^\]]+)\]\s*(.*)$/);
            if (categoryMatch) {
              const categoryLabel = categoryMatch[1];
              const remainingText = categoryMatch[2];
              
              // Find the category that matches the label
              const matchingCategory = feedbackCategories.find(cat => 
                cat.label.toLowerCase() === categoryLabel.toLowerCase()
              );
              
              if (matchingCategory) {
                setSelectedCategory(matchingCategory.value);
                setCustomFeedback(remainingText);
              } else {
                setCustomFeedback(feedbackText);
              }
            } else {
              setCustomFeedback(feedbackText);
            }
          } else {
            setExistingFeedback(null);
            setIsUpdatingExisting(false);
          }
        } catch (error) {
          console.error('Error checking existing feedback:', error);
          setExistingFeedback(null);
          setIsUpdatingExisting(false);
        } finally {
          setIsLoadingFeedback(false);
        }
      }
    };

    checkExistingFeedback();
  }, [showFeedbackModal, currentQuestion?.id]);

  // Reset form when modal closes
  useEffect(() => {
    if (!showFeedbackModal) {
      setSelectedCategory('');
      setCustomFeedback('');
      setExistingFeedback(null);
      setIsUpdatingExisting(false);
      setShowDropdown(false);
    }
  }, [showFeedbackModal]);

  if (!showFeedbackModal) return null;

  const handleSubmit = async () => {
    if (!selectedCategory && !customFeedback.trim()) {
      alert('Please select a category or provide custom feedback.');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Combine category and custom feedback
      const feedbackText = selectedCategory 
        ? `[${feedbackCategories.find(cat => cat.value === selectedCategory)?.label}] ${customFeedback.trim()}`
        : customFeedback.trim();

      await onSubmitFeedback(feedbackText);
      
      // Reset form
      setSelectedCategory('');
      setCustomFeedback('');
      setShowFeedbackModal(false);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleClose = () => {
    setSelectedCategory('');
    setCustomFeedback('');
    setShowDropdown(false);
    setExistingFeedback(null);
    setIsUpdatingExisting(false);
    setShowFeedbackModal(false);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category.value);
    setShowDropdown(false);
    
    // Pre-fill some text based on category
    if (category.value === 'content-issue') {
      setCustomFeedback('I found an issue with this question: ');
    } else if (category.value === 'technical-problem') {
      setCustomFeedback('I encountered a technical problem: ');
    } else if (category.value === 'answer-dispute') {
      setCustomFeedback('I disagree with the correct answer because: ');
    } else if (category.value === 'difficulty-level') {
      setCustomFeedback('The difficulty level seems inappropriate because: ');
    } else if (category.value === 'suggestion') {
      setCustomFeedback('I suggest: ');
    } else if (category.value === 'formatting') {
      setCustomFeedback('I noticed a formatting issue: ');
    }
  };

  const selectedCategoryData = feedbackCategories.find(cat => cat.value === selectedCategory);
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: themeStyles?.modal?.overlay?.background || (isDarkMode ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.5)'),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}
      onClick={handleClose}
    >      <div
        style={{
          backgroundColor: themeStyles?.modal?.content?.background || (isDarkMode ? '#151e30' : '#ffffff'),
          color: themeStyles?.modal?.content?.color || (isDarkMode ? '#ffffff' : '#2c3e50'),
          borderRadius: themeStyles?.modal?.content?.borderRadius || '12px',
          padding: '30px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
          position: 'relative'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',            background: 'transparent',
            border: 'none',
            color: themeStyles?.text?.secondary || (isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'),
            cursor: 'pointer',
            padding: '5px',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
          }}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>        {/* Header */}        <h2 style={{ 
          margin: '0 0 20px 0', 
          textAlign: 'center', 
          fontSize: '24px',
          color: themeStyles?.text?.primary || (isDarkMode ? '#ffffff' : '#2c3e50'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px'
        }}>
          <FontAwesomeIcon icon={faComment} />
          {isLoadingFeedback ? 'Loading Feedback...' : (isUpdatingExisting ? 'Update Feedback' : 'Submit Feedback')}
        </h2>{/* Feedback Category Selector */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            color: themeStyles?.text?.primary || (isDarkMode ? '#ffffff' : '#2c3e50'),
            fontWeight: '500',
            fontSize: '16px'
          }}>
            Feedback Category (Optional)
          </label>
          
          <div style={{ position: 'relative' }}>
            <button
              className="feedback-category-dropdown"
              onClick={() => setShowDropdown(!showDropdown)}
              style={{
                width: '100%',
                padding: '12px 15px',
                background: themeStyles?.input?.background || (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff'),
                color: themeStyles?.input?.color || (isDarkMode ? '#ffffff' : '#2c3e50'),
                border: themeStyles?.input?.border || (isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6'),
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '15px',
                transition: 'all 0.2s ease'
              }}
            >
              <span>
                {selectedCategoryData ? selectedCategoryData.label : 'Select a category...'}
              </span>
              <FontAwesomeIcon 
                icon={faChevronDown} 
                style={{ 
                  transform: showDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }} 
              />
            </button>
            
            {showDropdown && (
              <div style={{
                position: 'absolute',                top: '100%',
                left: 0,
                right: 0,
                background: themeStyles?.modal?.content?.background || (isDarkMode ? '#151e30' : '#ffffff'),
                border: themeStyles?.input?.border || (isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6'),
                borderRadius: '8px',
                marginTop: '4px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                zIndex: 10,
                maxHeight: '200px',
                overflowY: 'auto'
              }}>
                {feedbackCategories.map((category) => (
                  <button
                    key={category.value}
                    className="feedback-dropdown-item"
                    onClick={() => handleCategorySelect(category)}
                    style={{
                      width: '100%',
                      padding: '12px 15px',                      background: 'transparent',
                      border: 'none',
                      color: themeStyles?.text?.primary || (isDarkMode ? '#ffffff' : '#2c3e50'),
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                      transition: 'background 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'transparent';
                    }}
                  >
                    <div style={{ fontWeight: '500' }}>{category.label}</div>                    <div style={{ 
                      fontSize: '12px', 
                      color: themeStyles?.text?.secondary || (isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'),
                      marginTop: '2px'
                    }}>
                      {category.description}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {selectedCategoryData && (            <p style={{
              margin: '8px 0 0 0',
              fontSize: '13px',
              color: themeStyles?.text?.secondary || (isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'),
              fontStyle: 'italic'
            }}>
              {selectedCategoryData.description}
            </p>
          )}
        </div>

        {/* Custom Feedback Text Area */}
        <div style={{ marginBottom: '25px' }}>          <label style={{ 
            display: 'block',
            marginBottom: '8px',
            color: themeStyles?.text?.primary || (isDarkMode ? '#ffffff' : '#2c3e50'),
            fontWeight: '500',
            fontSize: '16px'
          }}>
            Your Feedback {!selectedCategory && <span style={{ color: '#e53e3e' }}>*</span>}
          </label>
          <textarea
            value={customFeedback}
            onChange={(e) => setCustomFeedback(e.target.value)}
            placeholder={selectedCategory 
              ? "Please provide additional details..." 
              : "Please describe your feedback in detail..."
            }
            maxLength={1000}            style={{
              width: '100%',
              minHeight: '120px',
              padding: '15px',
              background: themeStyles?.input?.background || (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#ffffff'),
              color: themeStyles?.input?.color || (isDarkMode ? '#ffffff' : '#2c3e50'),
              border: themeStyles?.input?.border || (isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6'),
              borderRadius: '8px',
              resize: 'vertical',
              fontFamily: 'inherit',
              fontSize: '15px',
              lineHeight: '1.5',
              boxSizing: 'border-box',
              transition: 'border-color 0.3s ease',
              outline: 'none'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = isDarkMode ? '#4f9eff' : '#2d70fd';
              e.target.style.boxShadow = `0 0 0 2px ${isDarkMode ? 'rgba(79, 158, 255, 0.2)' : 'rgba(45, 112, 253, 0.2)'}`;
            }}            onBlur={(e) => {
              e.target.style.borderColor = (themeStyles?.input?.border || (isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6')).split(' ')[2]; // Extract color from border string
              e.target.style.boxShadow = 'none';
            }}          />          
          {/* Character counter */}
          <div style={{
            textAlign: 'right',
            fontSize: '12px',
            color: themeStyles?.text?.secondary || (isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'),
            marginTop: '4px'
          }}>
            {customFeedback.length}/1000 characters
          </div>
          
          {/* Existing feedback indicator */}
          {isLoadingFeedback && (
            <div style={{
              fontSize: '13px',
              color: themeStyles?.text?.secondary || (isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'),
              marginTop: '8px',
              fontStyle: 'italic'
            }}>
              Loading existing feedback...
            </div>
          )}
          
          {existingFeedback && !isLoadingFeedback && (
            <div style={{
              fontSize: '13px',
              color: themeStyles?.text?.secondary || (isDarkMode ? '#e1e9ff' : 'rgba(44, 62, 80, 0.8)'),
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: '6px',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`
            }}>
              <strong>Note:</strong> You have previously submitted feedback for this question. Your changes will update the existing feedback.
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'center'
        }}>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || (!selectedCategory && !customFeedback.trim())}            style={{
              background: (!selectedCategory && !customFeedback.trim()) || isSubmitting
                ? (isDarkMode ? 'rgba(0, 116, 204, 0.3)' : 'rgba(25, 118, 210, 0.3)')
                : themeStyles?.button?.primary?.background || (isDarkMode ? '#0074cc' : '#1976d2'),
              color: themeStyles?.button?.primary?.color || '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: (!selectedCategory && !customFeedback.trim()) || isSubmitting ? 'not-allowed' : 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              minWidth: '120px'
            }}          >
            {isSubmitting ? 'Submitting...' : (isUpdatingExisting ? 'Update Feedback' : 'Submit Feedback')}
          </button>
          <button            onClick={handleClose}
            style={{
              background: themeStyles?.button?.secondary?.background || (isDarkMode ? 'rgba(255, 255, 255, 0.1)' : '#f1f3f5'),
              color: themeStyles?.button?.secondary?.color || (isDarkMode ? '#ffffff' : '#2c3e50'),
              border: themeStyles?.button?.secondary?.border || (isDarkMode ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #dcdfe6'),
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.3s ease',
              minWidth: '100px'
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
